/**
 * The score's percussion, rendered by the same synth as the sound effects and triggered by the
 * sequencer. A field snare is the heart of it (07 §1): tight, with the wires buzzing on top.
 */

import { PRIORITY, type SfxDef } from './types';

const drum = (patch: SfxDef['patch']): SfxDef => ({ patch, variants: 1, priority: PRIORITY.critical, gain: 1 });

export const DRUM_SFX = {
  'drum.kick': drum({
    layers: [
      { wave: 'sine', freq: 152, freqEnd: 42, sweep: 0.45, decay: 0.24, gain: 1 },
      { wave: 'noise', decay: 0.012, curve: 3, gain: 0.35, filter: { type: 'lp', cutoff: 2800 } },
    ],
    drive: 1.4,
  }),
  'drum.snare': drum({
    layers: [
      { wave: 'sine', freq: 215, freqEnd: 165, decay: 0.07, gain: 0.7 },
      { wave: 'noise', decay: 0.16, curve: 2.2, gain: 1, filter: { type: 'bp', cutoff: 2100, q: 0.8 } },
      { wave: 'noise', decay: 0.2, curve: 2.5, gain: 0.5, filter: { type: 'hp', cutoff: 3600 } },
    ],
    drive: 1.3,
  }),
  'drum.hat': drum({
    layers: [{ wave: 'noise', decay: 0.04, curve: 3, gain: 1, filter: { type: 'hp', cutoff: 7200 } }],
  }),
  /** Temperate's woody tom; the sequencer pitches it by playback rate. */
  'drum.tom': drum({
    layers: [
      { wave: 'sine', freq: 132, freqEnd: 84, sweep: 0.6, decay: 0.28, gain: 1 },
      { wave: 'noise', decay: 0.03, curve: 3, gain: 0.4, filter: { type: 'bp', cutoff: 900, q: 1.5 } },
    ],
  }),
  /** Desert's dry stick-on-rim click. */
  'drum.rim': drum({
    layers: [
      { wave: 'sine', freq: 890, fmRatio: 1.48, fmIndex: 2.2, fmDecay: 0.006, decay: 0.035, curve: 3, gain: 1 },
      { wave: 'noise', decay: 0.01, curve: 3, gain: 0.5, filter: { type: 'hp', cutoff: 2500 } },
    ],
  }),
} satisfies Record<string, SfxDef>;
